// src/components/MainApp.js
import React, {useState, useEffect} from 'react';
import {useAuth} from '../contexts/AuthContext';
import Login from './Login';
import TeamSelection from './TeamSelection';
import Game from './Game';
import Header from './Header';
import Stats from './Stats';
import TeamHistory from './TeamHistory'; // Nova komponenta
import BuyModal from './BuyModal';
import ShareModal from './ShareModal';
import AdminPanel from './AdminPanel';
import useTeamScores from '../hooks/useTeamScores';
import {useLanguage} from "../contexts/LanguageContext";

export default function MainApp() {
    const {user, userData, loading: authLoading, reloadUserData} = useAuth();
    const [showBuyModal, setShowBuyModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [shareLink, setShareLink] = useState('');
    const [gameScore, setGameScore] = useState(0);
    const [gameDailyScore, setGameDailyScore] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [timeLeft, setTimeLeft] = useState('');
    const {teamScores, loading: teamScoresLoading} = useTeamScores();
    const { t } = useLanguage();

    // Show loading if either auth or team scores are loading
    const isLoading = authLoading || teamScoresLoading;

    useEffect(() => {
        setGameScore(userData?.totalScore || 0);
        setGameDailyScore(userData?.dailyScore || 0);
    },[userData])

    // Countdown timer to midnight
    useEffect(() => {
        const updateCountdown = () => {
            const now = new Date();
            const midnight = new Date();
            midnight.setHours(24, 0, 0, 0); // Next midnight

            const timeDiff = midnight.getTime() - now.getTime();

            if (timeDiff <= 0) {
                setTimeLeft('00:00:00');
                return;
            }

            const hours = Math.floor(timeDiff / (1000 * 60 * 60));
            const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

            setTimeLeft(
                `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
            );
        };

        updateCountdown(); // Initial call
        const interval = setInterval(updateCountdown, 1000);

        return () => clearInterval(interval);
    }, []);

    // Show loading state until both userData and teamScores are ready
    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                    <div className="text-white text-lg">Loading game data...</div>
                </div>
            </div>
        );
    }

    if (!user) {
        return <Login/>;
    }

    if (!userData?.team) {
        return <TeamSelection/>;
    }

    const generateShareLink = () => {
        const link = `${window.location.origin}?ref=${user.uid}`;
        navigator.clipboard.writeText(link);
        setShareLink(link);
        setShowShareModal(true);
    };

    const handleRefreshData = async () => {
        setIsRefreshing(true);
        try {
            await reloadUserData();
            // Kratka notifikacija o uspešnom osvežavanju
            // Možete dodati toast notifikaciju ovde ako imate biblioteku za to
        } catch (error) {
            console.error('Error refreshing data:', error);
        } finally {
            setIsRefreshing(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white p-4">
            <div className="max-w-4xl mx-auto">
                <Header/>

                <div>
                    <Stats
                        gameScore={gameScore}
                        teamScores={teamScores}
                        gameDailyScore={gameDailyScore}
                        team={userData.team}
                        dailyAttempts={userData.dailyAttempts || 0}
                        bonusAttempts={userData.bonusAttempts || 0}
                    />

                    {/* Countdown to match end with background image */}
                    <div
                        className="mb-6 text-center relative rounded-lg p-4 shadow-lg overflow-hidden"
                        style={{
                            backgroundImage: 'url(/delije-vs-grobari.jpg)',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            minHeight: '140px'
                        }}
                    >
                        {/* Overlay za bolje čitanje teksta */}
                        <div className="absolute inset-0 bg-gradient-to-r from-red-600/80 to-orange-600/80 rounded-lg"></div>

                        {/* Sadržaj countdown-a */}
                        <div className="relative z-10">
                            <h3 className="text-lg font-semibold mb-2 text-white drop-shadow-lg">
                                ⏰ {t('matchEndsIn')}
                            </h3>
                            <div className="text-3xl font-bold text-white font-mono tracking-wider drop-shadow-lg">
                                {timeLeft}
                            </div>
                            <p className="text-sm text-orange-100 mt-2 drop-shadow">
                                {t('matchResetMidnight')}
                            </p>
                        </div>
                    </div>

                    <TeamHistory teamScores={teamScores} />

                    <Game
                        team={userData.team}
                        teamScores={teamScores}
                    />
                </div>

                <div className="flex gap-4 mt-6 justify-center flex-wrap">
                    <button
                        onClick={() => setShowBuyModal(true)}
                        className="px-6 py-3 bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        {t('buyAttempts')}
                    </button>
                    <button
                        onClick={generateShareLink}
                        className="px-6 py-3 bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                    >
                        {t('shareLink')}
                    </button>
                    <button
                        onClick={handleRefreshData}
                        disabled={isRefreshing}
                        className="px-6 py-3 bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {isRefreshing ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Refreshing...
                            </>
                        ) : (
                            <>
                                🔄 {t('checkPayment')}
                            </>
                        )}
                    </button>
                </div>
            </div>

            {showBuyModal && (
                <BuyModal onClose={() => setShowBuyModal(false)}/>
            )}

            {showShareModal && (
                <ShareModal link={shareLink} onClose={() => {
                    setShowShareModal(false)
                    setShareLink('')
                }}/>
            )}

            {userData.isAdmin && <AdminPanel/>}
        </div>
    );
}